import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banknote, Clock, Copy, History, MessageCircle, Send, Wallet, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { PinjamSaldoBanner } from "../components/PinjamSaldoBanner";
import { useAuth } from "../context/AuthContext";
import { ChatPanel } from "../components/ChatPanel";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";


export default function AgentTopup() {
  const { user: me } = useAuth();
  const [BANK_ACCOUNTS, setBankAccounts] = useState([]);
  useEffect(() => { api.get("/bank-accounts", { params: { scope: "deposit" } }).then((r) => setBankAccounts(r.data)).catch(() => {}); }, []);
  const ACCOUNT_NAME = BANK_ACCOUNTS[0]?.holder || "ABET NIGO";
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ amount: "", bank: "" });
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const bayarHutang = searchParams.get("bayar") === "hutang";
  useEffect(() => {
    const j = searchParams.get("jumlah");
    if (bayarHutang && j) setForm((f) => ({ ...f, amount: j }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pending, setPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [mutations, setMutations] = useState([]);
  const [showAllMut, setShowAllMut] = useState(false);

  const cancelRequest = async () => {
    try {
      await api.post(`/balance/topup-requests/${pending.id}/cancel`);
      toast.success("Request deposit dibatalkan");
      setConfirmCancel(false);
      setPending(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pending?.id]);

  const remainingMs = pending?.expired_at ? Math.max(0, new Date(pending.expired_at).getTime() - nowTs) : null;
  useEffect(() => {
    if (remainingMs === 0 && pending) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingMs === 0]);
  const mmss = remainingMs != null ? `${String(Math.floor(remainingMs / 60000)).padStart(2, "0")}:${String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, "0")}` : null;

  const [mutKind, setMutKind] = useState("");
  const load = (kind = mutKind) => {
    api.get("/balance/topup-requests").then((r) => {
      setRequests(r.data);
      setPending(r.data.find((x) => x.status === "pending") || null);
    }).catch(() => {});
    api.get("/balance/logs", { params: kind ? { kind } : {} }).then((r) => setMutations(r.data.slice(0, 30))).catch(() => {});
  };
  useEffect(load, []);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(String(text)); toast.success(`${label} disalin`); }
    catch { toast.error("Gagal menyalin"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/balance/topup-requests", { amount: Number(form.amount), bank: form.bank });
      toast.success("Request deposit dibuat — selesaikan transfer sesuai nominal");
      setPending(data);
      setUrgentOpen(true);
      setForm({ amount: "", bank: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSubmitting(false); }
  };

  const pendingBank = BANK_ACCOUNTS.find((b) => b.bank === pending?.bank);

  return (
    <div className="space-y-6" data-testid="topup-page">
      <Dialog open={urgentOpen && !!pending} onOpenChange={setUrgentOpen}>
        <DialogContent className="max-w-sm border-rose-500/50" data-testid="dialog-transfer-now">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading text-rose-500 flex items-center gap-2"><Clock className="w-5 h-5" /> Segera Transfer Sekarang!</DialogTitle>
            <DialogDescription className="text-xs">Request ini hanya berlaku <b>10 menit</b>. Lewat waktu itu request otomatis hangus dan harus dibuat ulang.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-center">
            <div className="text-xs text-muted-foreground">Transfer tepat hingga 3 digit terakhir:</div>
            <div className="font-mono text-3xl font-extrabold text-amber-500" data-testid="urgent-total">{rupiah(pending?.total_transfer)}</div>
            {pendingBank && <div className="text-sm">Ke <b>{pendingBank.bank}</b> <span className="font-mono">{pendingBank.number}</span> a.n. {ACCOUNT_NAME}</div>}
            {mmss && <div className="font-mono text-rose-500 text-sm font-bold" data-testid="urgent-countdown">Sisa waktu: {mmss}</div>}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" onClick={() => copy(pending?.total_transfer, "Nominal")} data-testid="urgent-copy"><Copy className="w-4 h-4 mr-1.5" /> Salin Nominal</Button>
              <Button className="bg-rose-600 hover:bg-rose-500 text-white" onClick={() => setUrgentOpen(false)} data-testid="urgent-ok">Saya Transfer Sekarang</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Isi Saldo</h1>
        <p className="text-sm text-muted-foreground mt-1">Request deposit saldo agen via transfer bank. Nominal transfer menyertakan kode unik 3 digit agar mudah dicek admin.</p>
      </div>

      <Card className="bg-gradient-to-br from-blue-600 to-blue-800 border-0 text-white shadow-md" data-testid="saldo-card">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><Wallet className="w-6 h-6" /></div>
          <div>
            <div className="text-xs text-blue-100">Saldo Saat Ini</div>
            <div className="font-mono text-2xl font-extrabold" data-testid="saldo-value">{rupiah(me?.balance || 0)}</div>
          </div>
        </CardContent>
      </Card>

      {pending ? (
        <Card className="bg-card border-amber-500/40 shadow-sm" data-testid="pending-card">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" /> Menunggu Konfirmasi Admin</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center space-y-1">
              <div className="text-xs text-muted-foreground">Transfer tepat hingga 3 digit terakhir:</div>
              <div className="inline-flex items-center gap-2">
                <span className="font-mono text-3xl font-extrabold text-amber-600" data-testid="pending-total">{rupiah(pending.total_transfer)}</span>
                <button type="button" onClick={() => copy(pending.total_transfer, `Nominal ${rupiah(pending.total_transfer)}`)} className="text-amber-600 hover:text-amber-500 transition-colors" data-testid="btn-copy-total">
                  <Copy className="w-5 h-5" />
                </button>
              </div>
              {mmss && (
                <div className="inline-flex items-center gap-1.5 mt-1 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-500 text-xs font-mono font-bold" data-testid="topup-countdown">
                  <Clock className="w-3.5 h-3.5" /> Sisa waktu transfer: {mmss} — lewat 10 menit request otomatis kedaluwarsa
                </div>
              )}
            </div>
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-center" data-testid="pending-bank">
              <div className="text-xs text-muted-foreground">Transfer ke:</div>
              <div className="inline-flex items-center gap-2">
                <span className="font-mono font-bold">{pending.bank} — {pendingBank?.number || "-"}</span>
                <button type="button" onClick={() => copy(pendingBank?.number || "", `No. rekening ${pending.bank}`)} className="text-amber-600 hover:text-amber-500 transition-colors" data-testid="btn-copy-rek">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs text-muted-foreground">an. {ACCOUNT_NAME}</div>
            </div>
            <div className="space-y-2" data-testid="topup-chat-wrap">
              <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-emerald-500" /> Konfirmasi via Chat Live — kirim bukti transfer (foto) di sini:</div>
              <ChatPanel channel={`agent:${me?.id}`} testid="topup-chat" />
            </div>
            {confirmCancel ? (
              <div className="flex items-center justify-center gap-2 text-sm" data-testid="cancel-confirm">
                <span className="text-muted-foreground">Yakin batalkan request ini?</span>
                <Button size="sm" variant="destructive" onClick={cancelRequest} data-testid="btn-confirm-cancel">Ya, Batalkan</Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmCancel(false)} data-testid="btn-keep-request">Tidak</Button>
              </div>
            ) : (
              <Button variant="ghost" className="w-full text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" onClick={() => setConfirmCancel(true)} data-testid="btn-cancel-topup">
                <XCircle className="w-4 h-4 mr-1.5" /> Batalkan Request
              </Button>
            )}
            <p className="text-[11px] text-center text-muted-foreground">Saldo otomatis masuk setelah admin menyetujui request Anda.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border shadow-sm" data-testid="topup-form-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-500" /> Request Deposit</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Nominal Deposit (min Rp 10.000)</Label>
                <Input type="number" min="10000" step="1000" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="cth: 100000" data-testid="input-topup-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Transfer ke Bank an. {ACCOUNT_NAME}, pilih Bank :</Label>
                <Select value={form.bank} onValueChange={(v) => setForm({ ...form, bank: v })} required>
                  <SelectTrigger data-testid="select-topup-bank"><SelectValue placeholder="Pilih bank tujuan" /></SelectTrigger>
                  <SelectContent>
                    {BANK_ACCOUNTS.map((b) => <SelectItem key={b.bank} value={b.bank} data-testid={`bank-opt-${b.bank}`}>{b.bank} — {b.number} an. {ACCOUNT_NAME}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" disabled={submitting || !form.bank || Number(form.amount) < 10000} data-testid="btn-submit-topup">
                <Send className="w-4 h-4 mr-1.5" /> {submitting ? "Memproses..." : "Buat Request Deposit"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="mutation-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><History className="w-4 h-4 text-emerald-500" /> Mutasi Transaksi</CardTitle></CardHeader>
        <div className="px-4 sm:px-6 pb-3"><PinjamSaldoBanner testid="agent-pinjam-saldo" /></div>
        <div className="px-4 sm:px-6 pb-3 flex items-center gap-1.5" data-testid="mutation-kind-filter">
          {[["", "Semua"], ["komisi", "Hanya Komisi"]].map(([v, label]) => (
            <button key={v || "all"} type="button"
              onClick={() => { setMutKind(v); setShowAllMut(false); load(v); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${mutKind === v ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "bg-transparent text-muted-foreground border-border hover:bg-accent"}`}
              data-testid={`btn-mut-kind-${v || "all"}`}>{label}</button>
          ))}
        </div>
        <Table className="w-full table-fixed" data-testid="mutation-table">
          <TableHeader>
            <TableRow>
              <TableHead>Keterangan</TableHead>
              <TableHead className="text-right">Masuk</TableHead>
              <TableHead className="text-right">Keluar</TableHead>
              <TableHead className="text-right">Saldo Akhir</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!mutations.length && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground" data-testid="mutation-empty">{mutKind === "komisi" ? "Belum ada mutasi komisi pada akun ini" : "Belum ada mutasi transaksi"}</TableCell></TableRow>}
            {(showAllMut ? mutations : mutations.slice(0, 5)).map((l) => (
              <TableRow key={l.id} data-testid={`mutation-row-${l.id}`}>
                <TableCell className="py-1 px-2">
                  <div className="text-[11px] font-medium break-words leading-snug">{l.note.replace(/ \(req [0-9a-f]+\)$/i, "")}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{fmtDateTime(l.created_at)}</div>
                </TableCell>
                <TableCell className="text-right font-mono font-semibold text-emerald-600 text-[11px] py-1 px-1">{l.amount > 0 ? `+${rupiah(l.amount)}` : "-"}{l.pct && <span className="ml-1 px-1 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-[9px] font-semibold" data-testid={`topup-log-pct-${l.id}`}>{l.pct}</span>}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-rose-500 text-[11px] py-1 px-1">{l.amount < 0 ? `-${rupiah(Math.abs(l.amount))}` : "-"}</TableCell>
                <TableCell className="text-right font-mono text-[11px] py-1 px-1">{l.balance_after != null ? rupiah(l.balance_after) : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {mutations.length > 5 && (
          <div className="border-t border-border">
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setShowAllMut(!showAllMut)} data-testid="btn-mutation-more">
              {showAllMut ? "Tampilkan lebih sedikit" : `Selengkapnya (${mutations.length - 5} mutasi lainnya)`}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
